import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, Injector, ComponentRef, ViewContainerRef, AfterViewInit, OnDestroy, Type, ChangeDetectionStrategy } from '@angular/core';
import { IDashboardWidget, GridWidgetConfig } from 'src/app/models/dashboard';
import { EMPTY_WIDGET_TITLE_META, WidgetTitleMeta } from 'src/app/models/dashboard/widget-title.model';
import { SampleListComponent } from 'src/app/components/sample-list/sample-list.component';
import { AgentsListComponent } from 'src/app/components/agents-list/agents-list.component';
import { AgentGroupListComponent } from 'src/app/components/agent-group-list/agent-group-list.component';
import { ControlsListComponent } from 'src/app/components/controls-list/controls-list.component';
import { LabRequisitionListComponent } from 'src/app/components/lab-requisition-list/lab-requisition-list.component';
import { SurveyListComponent } from 'src/app/components/survey-list/survey-list.component';
import { SegListComponent } from 'src/app/components/seg-list/seg-list.component';
import { PpeListComponent } from 'src/app/components/ppe-list/ppe-list.component';
import { UserListComponent } from 'src/app/components/user-list/user-list.component';
import { ReportListComponent } from 'src/app/components/report-list/report-list.component';
import { JurisdictionsListComponent } from 'src/app/components/jurisdictions-list/jurisdictions-list.component';
import { FileAttachmentListComponent } from 'src/app/components/file-attachment-list/file-attachment-list.component';
import { CalibrationEquipmentComponent } from 'src/app/components/calibration-equipment/calibration-equipment.component';
import { SamplingEquipmentComponent } from 'src/app/components/sampling-equipment/sampling-equipment.component';
import { CommonService } from 'src/app/services/common.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { WIDGET_CONFIG, BaseGridComponent } from 'src/app/shared/grid/baseGrid.component';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { WidgetOrgResolverService } from 'src/app/services/widget-org-resolver.service';
import { DashboardOverrideService } from 'src/app/services/dashboard-override.service';
import { ORG_LEVEL_OVERRIDE_KEY } from 'src/app/common-components/widget-org-filter/widget-org-filter.component';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type ViewIconType = 'default' | 'shared' | 'user';

interface HasSetSelectedOrg {
  setSelectedOrg(org: OrganizationLevel): void;
}

type EmbeddedGrid = Pick<BaseGridComponent<object>, 'widgetMode' | 'loadPage'>;

type GridComponentType = Type<EmbeddedGrid>;

const GRID_COMPONENT_MAP: Record<string, GridComponentType> = {
  [RolePermissionObjects.Sample]: SampleListComponent,
  [RolePermissionObjects.Agent]: AgentsListComponent,
  [RolePermissionObjects.AgentGroup]: AgentGroupListComponent,
  [RolePermissionObjects.Control]: ControlsListComponent,
  [RolePermissionObjects.LabRequisition]: LabRequisitionListComponent,
  [RolePermissionObjects.Survey]: SurveyListComponent,
  [RolePermissionObjects.SEG]: SegListComponent,
  [RolePermissionObjects.CalibrationEquipment]: CalibrationEquipmentComponent,
  [RolePermissionObjects.SamplingEquipment]: SamplingEquipmentComponent,
  [RolePermissionObjects.Ppe]: PpeListComponent,
  [RolePermissionObjects.User]: UserListComponent,
  [RolePermissionObjects.Report]: ReportListComponent,
  [RolePermissionObjects.Jurisdiction]: JurisdictionsListComponent,
  [RolePermissionObjects.FileAttachment]: FileAttachmentListComponent,
};

@Component({
  standalone: false,
  selector: 'app-grid-widget',
  templateUrl: './grid-widget.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./grid-widget.component.scss']
})
export class GridWidgetComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy, IDashboardWidget {
  @Input() widgetTitle?: string;
  @Input() titleMeta: WidgetTitleMeta = EMPTY_WIDGET_TITLE_META;
  @Input() widgetId?: string;
  @Input() widgetConfig: Record<string, unknown> = {};
  @Input() isLoading = false;

  @ViewChild('sampleListContainer', { read: ViewContainerRef }) containerRef!: ViewContainerRef;

  viewIcon?: ViewIconType;
  private gridComponentRef?: ComponentRef<EmbeddedGrid>;
  private destroy$ = new Subject<void>();
  private widgetConfig$ = new BehaviorSubject<Record<string, unknown>>({});

  constructor(
    private injector: Injector,
    private commonService: CommonService,
    private gridViewService: GridViewService,
    private widgetOrgResolver: WidgetOrgResolverService,
    private dashboardOverride: DashboardOverrideService,
  ) { }

  ngOnInit(): void {
    this.widgetConfig$.next(this.widgetConfig);
    this.loadViewIcon();
    this.widgetOrgResolver.effectiveOrgId$(this.widgetConfig$.asObservable())
      .pipe(takeUntil(this.destroy$))
      .subscribe(orgId => this.applyOrgToEmbeddedGrid(orgId));
    this.widgetOrgResolver.lostAccess$(this.widgetConfig$.asObservable())
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.widgetId != null) this.dashboardOverride.reportLostAccess(this.widgetId);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['widgetConfig'] && !changes['widgetConfig'].firstChange) {
      this.widgetConfig$.next(this.widgetConfig);
      const override = this.widgetConfig[ORG_LEVEL_OVERRIDE_KEY] as string | null | undefined;
      if (this.widgetId != null) {
        this.dashboardOverride.setOverride(this.widgetId, override ?? null);
      }
    }
  }

  private loadViewIcon(): void {
    const gridViewId = this.gridConfig?.gridViewId;
    if (!gridViewId) {
      this.viewIcon = 'default';
      return;
    }

    this.gridViewService.getById(gridViewId).subscribe({
      next: (view) => this.viewIcon = view.isShared ? 'shared' : 'user',
      error: () => this.viewIcon = 'user'
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initializeGridComponent(), 0);
  }

  private get gridConfig(): GridWidgetConfig | undefined {
    if (this.widgetConfig && this.widgetConfig['objectId']) {
      return this.widgetConfig as unknown as GridWidgetConfig;
    }
    return undefined;
  }

  private initializeGridComponent(): void {
    if (!this.containerRef) return;

    this.containerRef.clear();

    const objectId = (this.gridConfig?.objectId || RolePermissionObjects.Sample) as string;
    const gridViewId = this.gridConfig?.gridViewId;
    const componentType: GridComponentType = GRID_COMPONENT_MAP[objectId] || SampleListComponent;

    const widgetInjector = Injector.create({
      providers: [
        { provide: WIDGET_CONFIG, useValue: { widgetMode: true, widgetId: this.widgetId, gridViewId } }
      ],
      parent: this.injector
    });

    this.gridComponentRef = this.containerRef.createComponent(componentType, { injector: widgetInjector });

    const componentElement = this.gridComponentRef.location.nativeElement;
    this.containerRef.element.nativeElement.appendChild(componentElement);

    const instance = this.gridComponentRef.instance;
    instance.widgetMode = true;

    if (gridViewId && 'injectedGridViewId' in instance) {
      (instance as EmbeddedGrid & { injectedGridViewId: string | undefined }).injectedGridViewId = gridViewId;
    }

    const effectiveOrgId = this.widgetOrgResolver.getEffectiveOrgId(this.widgetConfig);
    const override = this.widgetConfig[ORG_LEVEL_OVERRIDE_KEY] as string | null | undefined;
    if (this.widgetId != null) {
      this.dashboardOverride.setOverride(this.widgetId, override ?? null);
    }
    this.applyOrgToEmbeddedGrid(effectiveOrgId);

    this.gridComponentRef.changeDetectorRef.detectChanges();
  }

  private applyOrgToEmbeddedGrid(orgId: string | undefined): void {
    const instance = this.gridComponentRef?.instance;
    if (!instance || !('setSelectedOrg' in instance)) return;

    const typedInstance = instance as EmbeddedGrid & HasSetSelectedOrg;
    if (orgId != null) {
      const currentOrg = this.commonService.selectedOrgEmitter.value;
      const org: OrganizationLevel = (currentOrg?.id === orgId ? currentOrg : { id: orgId } as OrganizationLevel);
      typedInstance.setSelectedOrg(org);
    } else {
      const currentOrg = this.commonService.selectedOrgEmitter.value;
      if (currentOrg) {
        typedInstance.setSelectedOrg(currentOrg);
      }
    }
  }

  refresh(): void {
    this.gridComponentRef?.instance?.loadPage();
  }

  ngOnDestroy(): void {
    if (this.widgetId != null) this.dashboardOverride.clearWidget(this.widgetId);
    this.destroy$.next();
    this.destroy$.complete();
    this.gridComponentRef?.destroy();
  }
}
